






























































import { Component, Mixins, Watch } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";
import { Brand, Company, Product, User } from "@/models";
import LogoWithFallback from "@/components/LogoWithFallback.vue";
import EntrySearch from "@/components/EntrySearch.vue";
import SpecialInitiativesMixin from "@/mixins/http/SpecialInitiatives";
import { COMPANY_TYPE } from "@/models/CompanyType";
import UserProfile from "@/components/UserProfile.vue";
import MeMixin, { GET_PROFILE_DATE } from "@/mixins/http/Agencies/Me";
import AdcontrolProfile from "@/models/AdcontrolProfile";
import { MutationMethod } from "vuex";
import Notifications from "./Notifications.vue";
import { formatApiDate } from "@/services/ApiDateHelper";
import {
  AUTOMATIC_REPORTS_ENABLED,
  FEATURE_AGENCIES_REPORT,
} from "@/constants";

@Component({
  components: { UserProfile, LogoWithFallback, EntrySearch, Notifications },
})
export default class TheTopbar extends Mixins(
  SpecialInitiativesMixin,
  MeMixin
) {
  @Getter("user") readonly user!: User;
  @Getter("profile") readonly profile!: AdcontrolProfile;

  @Mutation("setLastDate") readonly setLastDate!: MutationMethod;

  hasFeature(feature: string): boolean {
    return this.user?.hasFeature(feature) ?? false;
  }

  // The Settings section only hosts unreleased features, so the whole entry
  // point is hidden while AUTOMATIC_REPORTS_ENABLED is off. See src/constants.ts.
  get settingsVisible(): boolean {
    return (
      AUTOMATIC_REPORTS_ENABLED && this.hasFeature(FEATURE_AGENCIES_REPORT)
    );
  }

  private readonly REQUEST_DATE_TAG = "request_date";
  private lastDate = "";
  private grpLastDate = "12/34/5678";

  async mounted() {
    await this.loadDates();
  }

  @Watch("$route.name")
  async onRouteUpdate() {
    await this.loadDates();
  }

  @Watch("$store.state.settings.profile")
  async onStoreProfileChanged() {
    await this.loadDates();
  }

  private async loadDates() {
    if (this.profile) {
      this.lastDate = await this.loadProfileDate();
    } else {
      this.lastDate = await this.loadlastDate();
    }
    this.setLastDate(
      this.$moment(this.lastDate, "DD/MM/YYYY").format("DD/MM/YYYY")
    );
    this.grpLastDate = await this.loadGrpLastDate();
    if (
      this.$moment(this.lastDate, "DD/MM/YYYY").isBefore(
        this.$moment(this.grpLastDate, "DD/MM/YYYY")
      )
    ) {
      this.grpLastDate = this.lastDate;
    }
  }

  async loadlastDate(): Promise<string> {
    if (
      this.$route.meta.specialInitiatives &&
      this.$route.meta.specialInitiatives === true
    ) {
      const res = await this.getLastPublishedDate(this.REQUEST_DATE_TAG);
      return formatApiDate(res.date, "DD/MM/YYYY");
    }
    if (this.user?.profile?.lastDate == undefined) {
      return "";
    }

    return formatApiDate(this.user?.profile.lastDate, "DD/MM/YYYY");
  }

  async loadProfileDate(): Promise<string> {
    const date = await this.getProfileLastDate(
      this.profile.slug,
      GET_PROFILE_DATE
    );
    if (!date.meta.date) {
      return "";
    } else {
      return formatApiDate(date.meta.date, "DD/MM/YYYY");
    }
  }

  async loadGrpLastDate(): Promise<string> {
    const date = this.$moment(
      this.user?.profile?.grpLastDate,
      "YYYY-MM-DD",
      true
    );
    return date.isValid() ? date.format("DD/MM/YYYY") : "-";
  }

  get isAdmin(): boolean {
    if (!this.user) {
      return false;
    }

    return this.user.isAdmin;
  }

  get isAdSalesAgency(): boolean {
    if (!this.user) {
      return false;
    }
    return (
      this.user.profile?.company.companyType == COMPANY_TYPE.AD_SALES_AGENCY ||
      this.user.profile?.company.companyType == COMPANY_TYPE.TV_AD_SALES_AGENCY
    );
  }

  get profileHasAuditel(): boolean {
    return this.user.profile?.hasFeature("auditel") ?? false;
  }

  get searchPlaceholder(): string {
    if (!this.isAdmin && this.$route.meta.specialInitiatives) {
      return "Cerca iniziative speciali";
    } else {
      return "Cerca aziende/brand/prodotti";
    }
  }

  onEntrySelected(entry: Company | Brand | Product) {
    if (
      !this.isAdmin &&
      (this.isAdSalesAgency || this.$route.meta.specialInitiatives)
    ) {
      if (entry instanceof Company) {
        this.$router.push({
          name: "SpecialInitiativesCompany",
          params: {
            companyId: entry.id,
          },
        });
      }
      if (entry instanceof Brand) {
        this.$router.push({
          name: "SpecialInitiativesBrand",
          params: {
            brandId: entry.id,
          },
        });
      }
      if (entry instanceof Product) {
        this.$router.push({
          name: "SpecialInitiativesProduct",
          params: {
            productId: entry.id,
          },
        });
      }
    } else {
      this.$router.push({
        name: "InvestorSummary",
        params: {
          type: entry.type,
          id: entry.id,
        },
      });
    }

    (this.$refs.search as EntrySearch).clearSearch();
  }
}
